import React, { PropsWithChildren } from 'react';
import Loader from '../../atoms/loader/Loader';
import Pagination from '../../organism/pagination/Pagination';
import { TABLE_PAGE_SIZES } from 'constants/options/paginationOptions';
import { RenderColumns } from './RenderColumns/RenderColumns';
import { TableColumns, TableSort } from './types';
import { classNames } from 'utils/helper/string';

export * from './constants';
export * from './types';

export interface RenderTableProps extends PropsWithChildren<any> {
  isLoading?: boolean;
  authError?: boolean;
  dataCount?: number;
  rowCount?: number;
  columns: TableColumns;
  totalCount?: number;
  totalPages?: number;
  pagination?: { perPage: number; currentPage: number };
  setPagination?: (args: { perPage: number; currentPage: number }) => void;
  paginationOptions?: { key: string; value: number }[];
  sort?: TableSort;
  setSort?: (sort: TableSort) => void;
  scrollToTop?: boolean;
  stickyScroll?: boolean;
  fullScreenAt?: 'mobile' | 'tablet' | 'none';
  compactPlaceholder?: boolean;
  isSubTable?: boolean;
  className?: string;
}

const wrapperClasses = 'my-6 flex w-[100vw] -translate-x-[var(--mobile-content-x-padding)] flex-col overflow-hidden rounded-none border-y border-gray-line';

const RenderTable = ({
  isLoading,
  authError = false,
  dataCount,
  children,
  rowCount = 10,
  columns = [],
  fullScreenAt = 'mobile',
  totalCount,
  totalPages,
  pagination,
  setPagination,
  isSubTable,
  className,
  compactPlaceholder = false,
  sort,
  setSort,
  paginationOptions = TABLE_PAGE_SIZES,
  scrollToTop = false,
  stickyScroll = Boolean(pagination),
}: RenderTableProps) => {
  const hasRows = !isLoading && dataCount > 0;

  return (
    <>
      {isLoading ? (
        <Loader
          columnCount={columns.length}
          rowCount={rowCount}
        />
      ) : (
        <div
          className={classNames(wrapperClasses, {
            'flex-grow': !hasRows,
            '!border-b-0': !pagination,
            'md:w-full md:translate-x-0 md:rounded-lg md:border': fullScreenAt === 'mobile',
            'lg:w-full lg:translate-x-0 lg:rounded-lg lg:border': fullScreenAt === 'tablet',
            'w-full translate-x-0 rounded-lg border': fullScreenAt === 'none',
            '!my-0 w-full !translate-x-0 !rounded-none !border-0': isSubTable,
            'max-h-[calc(100vh-140px)]': stickyScroll,
          }, className)}>
          <div className={`overflow-x-auto ${stickyScroll ? 'overflow-y-auto' : 'overflow-y-hidden'}`}>
            <table className='w-full bg-gray-3'>
              <thead
                className={`border-b border-gray-line bg-background-header text-xs font-semibold text-gray-1 ${!hasRows && 'hidden lg:table-header-group'} ${stickyScroll ? 'sticky top-0 z-10' : ''}`}>
                <tr>
                  <RenderColumns
                    columns={columns}
                    isSubTable={isSubTable}
                    sort={sort}
                    setSort={setSort}
                  />
                </tr>
              </thead>

              {hasRows && <tbody>{children}</tbody>}
            </table>
          </div>

          {!isLoading && !hasRows && !authError && (
            <div className={`flex w-full flex-grow flex-col justify-center bg-gray-3 text-center text-sm text-gray-1 ${compactPlaceholder ? 'py-12' : 'py-36 lg:py-80'}`}>
              No data found
            </div>
          )}

          {authError && (
            <div className={`flex w-full flex-grow flex-col justify-center bg-gray-3 text-center text-sm text-gray-1 ${compactPlaceholder ? 'py-12' : 'py-36 lg:py-80'}`}>
              Permissions required. Contact the administrator.
            </div>
          )}

          {!!pagination && hasRows && !authError && (
            <Pagination
              totalCount={totalCount}
              totalPages={totalPages}
              pagination={pagination}
              setPagination={setPagination}
              paginationOptions={paginationOptions}
              scrollToTop={scrollToTop}
            />
          )}
        </div>
      )}
    </>
  );
};

export default RenderTable;
